import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import GoogleButton from "@/components/GoogleButton";
import { api, formatApiError } from "@/lib/api";
import { X, ShieldAlert } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [banned, setBanned] = useState(false);
  const [showAppeal, setShowAppeal] = useState(false);
  const [appealMsg, setAppealMsg] = useState("");
  const [appealBusy, setAppealBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      nav("/");
    } else {
      const err = (res.error || "").toLowerCase();
      if (err.includes("banned") || err.includes("ban")) {
        setBanned(true);
      }
      toast.error(res.error);
    }
  };

  const submitAppeal = async () => {
    if (appealMsg.trim().length < 10) { toast.error("Please write a message of at least 10 characters"); return; }
    setAppealBusy(true);
    try {
      await api.post("/auth/ban-appeal", { email, password, message: appealMsg });
      toast.success("Appeal submitted — a moderator will review it soon.");
      setShowAppeal(false); setAppealMsg(""); setBanned(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setAppealBusy(false);
  };

  return (
    <div className="min-h-[calc(100vh-72px)] grid lg:grid-cols-2">
      <div
        className="hidden lg:flex items-center justify-center relative"
        style={{
          background: "linear-gradient(135deg, #FFF6EE 0%, #FFE9DC 100%)",
        }}
      >
        <img
          src="/brand/logo.png"
          alt="Cake Pop Talk"
          className="w-[min(90%,620px)] drop-shadow-2xl select-none pointer-events-none"
          data-testid="login-brand-logo"
        />
      </div>
      <div className="grid place-items-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-widest text-[#857163] mb-2">Welcome back</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24]">
              Log in to <span className="text-[#FF7E67]">Cake Pop Talk</span>
            </h1>
            <p className="mt-3 text-[#857163]">The forum where cake pop bakers gather.</p>
          </div>

          <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Email</label>
          <input
            type="email"
            className="cp-input mb-4"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="login-email"
          />

          <label className="block text-sm font-medium text-[#3B2C24] mb-1.5">Password</label>
          <input
            type="password"
            className="cp-input mb-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            data-testid="login-password"
          />
          <div className="mb-6 text-right">
            <Link to="/forgot-password" className="text-xs text-[#FF7E67] font-semibold hover:underline" data-testid="link-forgot-password">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="cp-btn-primary w-full disabled:opacity-70"
            data-testid="login-submit"
          >
            {loading ? "Logging in…" : "Log in"}
          </button>

          {banned && (
            <button
              type="button"
              onClick={() => setShowAppeal(true)}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border border-[#C2185B] text-[#C2185B] hover:bg-[#C2185B]/5 transition"
              data-testid="open-appeal-btn"
            >
              <ShieldAlert className="w-3.5 h-3.5" /> Appeal your ban
            </button>
          )}

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#EBDCD1]" />
            <span className="text-xs uppercase tracking-widest text-[#857163]">or</span>
            <div className="flex-1 h-px bg-[#EBDCD1]" />
          </div>
          <GoogleButton testId="google-signin-login" />

          <p className="mt-6 text-sm text-[#857163] text-center">
            New here?{" "}
            <Link to="/register" className="text-[#FF7E67] font-semibold hover:underline" data-testid="link-to-register">
              Create an account
            </Link>
          </p>
        </form>
      </div>

      {showAppeal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !appealBusy && setShowAppeal(false)} data-testid="appeal-modal">
          <div className="bg-white rounded-3xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#C2185B]">
                  <ShieldAlert className="w-3.5 h-3.5" /> <span>Ban appeal</span>
                </div>
                <h2 className="font-heading text-2xl text-[#3B2C24] mt-1">Explain your side</h2>
              </div>
              <button onClick={() => !appealBusy && setShowAppeal(false)} className="p-1.5 hover:bg-[#F9EFE6] rounded-full" data-testid="close-appeal"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-[#857163] mb-3">A moderator will read this and decide whether to lift or shorten your ban. Be honest and specific — what happened and why it won't repeat.</p>
            <textarea
              value={appealMsg}
              onChange={(e) => setAppealMsg(e.target.value)}
              className="cp-input min-h-[160px] resize-y text-sm"
              placeholder="Tell us what happened…"
              maxLength={2000}
              data-testid="appeal-message"
            />
            <div className="mt-1 text-[11px] text-[#857163] text-right">{appealMsg.length} / 2000</div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setShowAppeal(false)} className="cp-btn-ghost" disabled={appealBusy}>Cancel</button>
              <button onClick={submitAppeal} disabled={appealBusy || appealMsg.trim().length < 10} className="cp-btn-primary disabled:opacity-50" data-testid="submit-appeal">
                {appealBusy ? "Submitting…" : "Submit appeal"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
